/**
 * @license
 * @preserve
 *
 * KeeeX SAS Confidential
 * https://keeex.me
 * Copyright 2013-2022 KeeeX All Rights Reserved.
 * 
 * These computer program listings and specifications, herein,
 * are and remain the property of KeeeX SAS. The intellectual
 * and technical concepts herein are proprietary to KeeeX SAS
 * and may be covered by EU and foreign patents,
 * patents in process, trade secrets and copyright law.
 * 
 * They shall not be reproduced or copied or used in whole or
 * in part as the basis for manufacture or sale of items unless
 * prior written permission is obtained from KeeeX SAS.
 * 
 * For a license agreement, please contact:
 * <mailto: contact@keeex.net>
 * 
 */
import { getProvider } from "./provider.js";
import { randomBytes } from "./random.js";
/**
 * Use random source to generate a random key
 */
const fallbackGenerateCryptoKey = async (algorithm, keyLen) => {
    const keysProvider = getProvider().keysProvider;
    const randomData = await randomBytes(keyLen);
    return keysProvider.importCryptoKey(algorithm, randomData);
};
/**
 * Generate a random CryptoKey
 */
export const generateCryptoKey = (algorithm, keyLen) => {
    const keysProvider = getProvider().keysProvider;
    if (keysProvider.generateCryptoKey) {
        return keysProvider.generateCryptoKey(algorithm, keyLen);
    }
    return fallbackGenerateCryptoKey(algorithm, keyLen);
};
/**
 * Transform raw data into a CryptoKey instance
 */
export const importCryptoKey = (algorithm, keyData) => getProvider().keysProvider.importCryptoKey(algorithm, keyData);
/**
 * Use deriveKeyRaw() and importCryptoKey() as fallback to provide deriveKey()
 */
const fallbackDeriveKey = async (data, salt, digestAlgorithm, rounds, targetAlgorithm, keyLen) => {
    const keysProvider = getProvider().keysProvider;
    const keyData = await keysProvider.deriveKeyRaw(data, salt, digestAlgorithm, rounds, keyLen);
    return keysProvider.importCryptoKey(targetAlgorithm, keyData);
};
/**
 * Create a CryptoKey from a low-entropy input using PBKDF2
 */
export const deriveKey = (data, salt, digestAlgorithm, rounds, targetAlgorithm, keyLen) => {
    const keysProvider = getProvider().keysProvider;
    if (keysProvider.deriveKey) {
        return keysProvider.deriveKey(data, salt, digestAlgorithm, rounds, targetAlgorithm, keyLen);
    }
    return fallbackDeriveKey(data, salt, digestAlgorithm, rounds, targetAlgorithm, keyLen);
};
/**
 * Derive a fixed-size buffer from a low entropy source using PBKDF2
 */
export const deriveKeyRaw = (data, salt, digestAlgorithm, rounds, outLen) => getProvider().keysProvider.deriveKeyRaw(data, salt, digestAlgorithm, rounds, outLen);
